import { IeLynxObject } from "../../common/models/eLynxObject";
import { RestError } from "../../services/rest-service/RestService";
import { useMutation, useQueryClient } from "react-query";
import { IMemoModel } from "../../common/models/IMemoModel";
import EventsDataService from "../../services/events-data-service/EventsDataService";
import { IDiagnosticEventModel, IObjectHistoryModel, IWaterLossEventModel } from "../../services/history-data-service/Models";
import MemosDataService from "../../services/memos-data-service/MemosDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { IObjectHistoryDiagnosticEventItem, IObjectHistoryItem, IObjectHistoryMemoItem, IObjectHistoryWaterLossEventItem, MutationContextType, ObjectHistoryItemTypeEnum, toDiagnosticEventModel, toMemoModel, toWaterLossEventModel } from "./Common";
import { keys } from "./Keys";
import { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";

export type UseSaveHistoryItemParameters = {
    customerID: number,
    groupID: number,
    contextObject: IeLynxObject,
    duration?: DurationOptionEnum,
    startDate?: Date,
    endDate?: Date,
} & BaseUseMutationParameters<IMemoModel | IDiagnosticEventModel | IWaterLossEventModel | undefined, IObjectHistoryItem>;

export function useSaveHistoryItem({
    customerID,
    groupID,
    contextObject,
    offline,
    duration,
    startDate,
    endDate,
    onError,
    onSuccess
}: UseSaveHistoryItemParameters) {
    const queryClient = useQueryClient();

    return useMutation<IMemoModel | IDiagnosticEventModel | IWaterLossEventModel | undefined, RestError, IObjectHistoryItem, MutationContextType>(
        (item) => {
            switch (item.type) {
                case ObjectHistoryItemTypeEnum.Memo:
                    const memoModel = toMemoModel(item as IObjectHistoryMemoItem);

                    return MemosDataService.saveMemo(
                        customerID,
                        groupID,
                        memoModel,
                        offline ?? false
                    );

                case ObjectHistoryItemTypeEnum.DiagnosticEvent: {
                    const eventModel = toDiagnosticEventModel(item as IObjectHistoryDiagnosticEventItem);

                    return EventsDataService.saveDiagnosticEvent(
                        groupID,
                        eventModel
                    );
                }

                case ObjectHistoryItemTypeEnum.WaterLossEvent: {
                    const eventModel = toWaterLossEventModel(item as IObjectHistoryWaterLossEventItem);

                    return EventsDataService.saveWaterLossEvent(
                        groupID,
                        eventModel
                    );
                }

                default:
                    throw new Error("Cannot save specified history item type. Only memos, diagnostic events, and water loss events are supported at this time.");
            }
        },
        {
            onMutate: async (item: IObjectHistoryItem) => {

                await queryClient.cancelQueries(keys.all);

                const previousHistoryItems = queryClient.getQueryData<IObjectHistoryModel>(
                    keys.list(
                        customerID,
                        groupID,
                        contextObject,
                        offline ?? false,
                        duration,
                        startDate,
                        endDate
                    )
                );

                queryClient.setQueryData<IObjectHistoryModel>(
                    keys.list(
                        customerID,
                        groupID,
                        contextObject,
                        offline ?? false,
                        duration,
                        startDate,
                        endDate
                    ),
                    (old?: IObjectHistoryModel) => {

                        switch (item.type) {
                            case ObjectHistoryItemTypeEnum.Memo:
                                const memo = item as IObjectHistoryMemoItem;
                                const existingMemo = old?.memos.find(m => m.memoID === memo.memoID);

                                return existingMemo
                                    ? {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: old?.diagnosticEvents ?? [],
                                        formEvents: old?.formEvents ?? [],
                                        memos: old?.memos.map(m => (m.memoID === memo.memoID ? memo : m)) ?? []
                                    } as IObjectHistoryModel
                                    : {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: old?.diagnosticEvents ?? [],
                                        formEvents: old?.formEvents ?? [],
                                        memos: [...(old?.memos ?? []), item]
                                    } as IObjectHistoryModel;

                            case ObjectHistoryItemTypeEnum.DiagnosticEvent: {
                                const event = item as IObjectHistoryDiagnosticEventItem;
                                const existingEvent = old?.diagnosticEvents.find(e => e.id === event.eventID);

                                return existingEvent
                                    ? {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: old?.diagnosticEvents?.map(e => (e.id === event.eventID ? event : e)) ?? [],
                                        formEvents: old?.formEvents ?? [],
                                        memos: old?.memos ?? []
                                    } as IObjectHistoryModel
                                    : {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: [...(old?.diagnosticEvents ?? []), event],
                                        formEvents: old?.formEvents ?? [],
                                        memos: old?.memos ?? []
                                    } as IObjectHistoryModel;
                            }

                            case ObjectHistoryItemTypeEnum.WaterLossEvent: {
                                const event = item as IObjectHistoryWaterLossEventItem;
                                const existingEvent = old?.waterLossEvents.find(e => e.id === event.id);

                                return existingEvent
                                    ? {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: old?.diagnosticEvents ?? [],
                                        formEvents: old?.formEvents ?? [],
                                        memos: old?.memos ?? [],
                                        waterLossEvents: old?.waterLossEvents?.map(e => (e.id === event.id ? event : e)) ?? [],
                                    } as IObjectHistoryModel
                                    : {
                                        alarmHistory: old?.alarmHistory ?? [],
                                        controlHistory: old?.controlHistory ?? [],
                                        dataQualityEvents: old?.dataQualityEvents ?? [],
                                        diagnosticEvents: old?.diagnosticEvents ?? [],
                                        formEvents: old?.formEvents ?? [],
                                        memos: old?.memos ?? [],
                                        waterLossEvents: [...(old?.waterLossEvents ?? []), event],
                                    } as IObjectHistoryModel;
                            }

                            default:
                                throw new Error("Cannot save specified history item type. Only memos, diagnostic events, and water loss events are supported at this time.");
                        }
                    }
                );

                return {
                    previousHistoryItems
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _item, context) => {
                if (error && context?.previousHistoryItems) {
                    queryClient.setQueryData<IObjectHistoryModel>(
                        keys.list(
                            customerID,
                            groupID,
                            contextObject,
                            offline ?? false,
                            duration,
                            startDate,
                            endDate
                        ),
                        context?.previousHistoryItems
                    );
                }

                queryClient.invalidateQueries(keys.lists());
            }
        }
    );
}
